import React, { useEffect, useId, useState } from 'react';
import { Box, Button } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from 'components/Dialog';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { closeModal, selectActiveModalData } from 'features/modal/modalSlice';
import {
  selectActiveLibraryType,
  selectFile,
  selectFolder,
} from 'features/content-library/contentLibrarySlice';
import { useContentLibrary } from 'features/content-library/useContentLibrary';
import {
  moveContentFolderRequested,
  moveContentLibraryFileRequested,
} from 'features/content-library/actions';
import { FileType, MoveContentLibraryItemPayload } from 'features/content-library/types';
import MoveDestinationPicker from 'features/content-library/modals/move-picker/MoveDestinationPicker';

// Fixed rather than a max so entering a near-empty folder does not resize the dialog.
const PICKER_HEIGHT = 240;

const getDescriptionKey = (isFolder: boolean, fileType?: FileType) => {
  if (isFolder) {
    return 'content_library.move_item.folder_description';
  }

  return fileType === 'whiteboard'
    ? 'content_library.move_item.whiteboard_description'
    : 'content_library.move_item.description';
};

const MoveContentLibraryItemModal = () => {
  const { t } = useTranslation('modals');
  const dispatch = useAppDispatch();

  const modalId = useId();

  const modalData = useAppSelector(selectActiveModalData);
  const itemId = modalData.itemId as string;
  const isFolder = modalData.isFolder as boolean;

  const [cursor, setCursor] = useState('root');

  const file = useAppSelector((state) => selectFile(state, itemId));
  const folder = useAppSelector((state) => selectFolder(state, itemId));
  const cursorFolder = useAppSelector((state) => selectFolder(state, cursor));
  const activeLibraryType = useAppSelector(selectActiveLibraryType);

  const { isFeatureAvailable, canManageLibrary, canManagePersonalContentLibrary } =
    useContentLibrary();

  const item = isFolder ? folder : file;
  const currentParentId = isFolder ? folder?.parentId : file?.folderId;

  const canManageActiveLibrary =
    activeLibraryType === 'personal' ? canManagePersonalContentLibrary : canManageLibrary;

  const shouldClose = !isFeatureAvailable || !canManageActiveLibrary || !item;

  useEffect(() => {
    if (shouldClose) {
      dispatch(closeModal('moveContentLibraryItem'));
    }
  }, [shouldClose, dispatch]);

  // Another moderator can delete the folder being browsed while this modal is open.
  useEffect(() => {
    if (!cursorFolder) {
      setCursor('root');
    }
  }, [cursorFolder]);

  const handleCancel = () => {
    dispatch(closeModal('moveContentLibraryItem'));
  };

  const handleOk = () => {
    if (!item || !cursorFolder) {
      return;
    }

    const payload: MoveContentLibraryItemPayload = {
      id: itemId,
      parentId: cursor === 'root' ? undefined : cursor,
      personal: item.personal,
    };

    dispatch(
      isFolder ? moveContentFolderRequested(payload) : moveContentLibraryFileRequested(payload)
    );
    dispatch(closeModal('moveContentLibraryItem'));
  };

  return (
    <Dialog
      open={!shouldClose}
      maxWidth="xs"
      aria-labelledby={`${modalId}-title`}
      aria-describedby={`${modalId}-description`}
      onClose={handleCancel}
    >
      <DialogTitle id={`${modalId}-title`}>{t('content_library.move_item.title')}</DialogTitle>
      <DialogContent>
        <DialogContentText id={`${modalId}-description`}>
          <Trans
            i18nKey={`modals:${getDescriptionKey(isFolder, file?.type)}`}
            t={t}
            values={{ name: item?.name }}
            components={{
              bold: <strong />,
            }}
          />
        </DialogContentText>
        <Box
          sx={{
            mt: 4,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            height: PICKER_HEIGHT,
            overflowY: 'auto',
          }}
        >
          <MoveDestinationPicker folderId={cursor} excludeId={itemId} onEnterFolder={setCursor} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} color="grayscale">
          {t('content_library.move_item.btn_cancel_label')}
        </Button>
        <Button onClick={handleOk} disabled={cursor === currentParentId}>
          {t('content_library.move_item.btn_ok_label')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MoveContentLibraryItemModal;
